import { useEffect, useState } from "react";
import { Plus, Star, Trash2, Users, X } from "lucide-react";
import { Select } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../common/ToastMsg";
import { ASSIGNMENT_CAPACITY_URL, ASSIGNMENT_TEAMS_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { formatDateTime } from "../../utils/datetime";
import { Field, Pill, STATUS_META, Switch, btnOutline, btnPrimary, inputCls } from "./shared";

/**
 * Teams (who shares leads, with weights) and People (each rep's availability
 * and limits across all their teams). Team managers can edit their own team.
 */
export default function TeamsTab({ users, onChanged }) {
    const { fetch, create, update, remove } = useApiMethods();
    const [teams, setTeams] = useState(null);
    const [people, setPeople] = useState(null);
    const [canEdit, setCanEdit] = useState(false);
    const [defaults, setDefaults] = useState({});
    const [editing, setEditing] = useState(null);
    const [teamFilter, setTeamFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const loadTeams = () => fetch(ASSIGNMENT_TEAMS_URL).then(res => {
        if (res?.status === "success") { setTeams(res.teams); setCanEdit(res.can_edit); }
        else ToastMsg("error", res?.message || "Could not load teams");
    });
    const loadPeople = () => fetch(ASSIGNMENT_CAPACITY_URL, { team_id: teamFilter }).then(res => {
        if (res?.status === "success") { setPeople(res.people); setDefaults(res.defaults || {}); }
    });
    useEffect(() => { loadTeams(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { loadPeople(); }, [teamFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const savePerson = async (p, patch) => {
        const res = await update(`${ASSIGNMENT_CAPACITY_URL}${p.user_id}/`, patch);
        if (res?.status === "success") {
            setPeople(ps => ps.map(x => x.user_id === p.user_id ? res.person : x));
            onChanged?.();
        } else ToastMsg("error", res?.errors ? Object.values(res.errors)[0][0] : res?.message || "Could not save");
    };

    const delTeam = async (t) => {
        if (!window.confirm(`Delete the team "${t.name}"?${t.rule_count ? `\n\n${t.rule_count} rule(s) use it and will fall back until you change them.` : ""}`)) return;
        const res = await remove(`${ASSIGNMENT_TEAMS_URL}${t.id}/`);
        if (res?.status === "success") { ToastMsg("success", res.message); loadTeams(); onChanged?.(); }
        else ToastMsg("error", res?.message || "Could not delete");
    };

    if (!teams) return <p className="text-xs text-gray-400 p-4">Loading…</p>;
    const shownPeople = (people || []).filter(p => !statusFilter || p.status === statusFilter);

    return (
        <div className="space-y-6">
            <section>
                <div className="flex items-center justify-between gap-2 mb-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                        <Users className="h-4 w-4 text-primary" /> Teams
                    </h3>
                    {canEdit && (
                        <button type="button" className={btnPrimary}
                            onClick={() => setEditing({ name: "", description: "", manager_id: null, members: [] })}>
                            <Plus className="h-3.5 w-3.5" /> New team
                        </button>
                    )}
                </div>
                {!teams.length ? (
                    <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-6 text-center text-xs text-gray-500">
                        Create your first team — add the reps who share leads.
                    </div>
                ) : (
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        {teams.map(t => (
                            <div key={t.id} className={cn("rounded-lg border p-3 border-gray-200 dark:border-gray-700", !t.is_active && "opacity-60")}>
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1">
                                            {t.name} {t.is_default && <Star className="h-3 w-3 text-amber-500" title="Default team" />}
                                        </p>
                                        <p className="text-[11px] text-gray-500">
                                            Manager: {t.manager?.name || "none"} · {t.members.length} member{t.members.length === 1 ? "" : "s"}
                                            {t.rule_count ? ` · used by ${t.rule_count} rule${t.rule_count === 1 ? "" : "s"}` : ""}
                                        </p>
                                    </div>
                                    <div className="flex gap-1">
                                        {t.can_manage && <button type="button" className="text-[11px] text-primary hover:underline" onClick={() => setEditing(t)}>Edit</button>}
                                        {canEdit && !t.is_default && (
                                            <button type="button" title="Delete team" onClick={() => delTeam(t)}
                                                className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 className="h-3.5 w-3.5" /></button>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {t.members.map(m => (
                                        <span key={m.user_id} title={m.reason || `Last lead ${m.last_assigned_at ? formatDateTime(m.last_assigned_at) : "never"}`}
                                            className={cn("px-1.5 py-0.5 rounded text-[10px] border",
                                                m.eligible ? "border-emerald-200 text-emerald-700 dark:border-emerald-800 dark:text-emerald-300"
                                                    : "border-gray-200 text-gray-400 dark:border-gray-700")}>
                                            {m.name}{m.weight > 1 ? ` ×${m.weight}` : ""} · {m.today} today
                                            {!m.in_rotation && " (out of rotation)"}
                                        </span>
                                    ))}
                                    {!t.members.length && <span className="text-[11px] text-gray-400">No members yet</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <section>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">People</h3>
                        <p className="text-[11px] text-gray-500">
                            Limits apply across all of a rep's teams. Blank uses the default
                            ({defaults.daily_cap ? `${defaults.daily_cap} a day` : "no daily limit"},{" "}
                            {defaults.open_cap ? `${defaults.open_cap} open` : "no open limit"}).
                            Reps can set their own availability; only managers set limits.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <select value={teamFilter} onChange={e => setTeamFilter(e.target.value)} className={cn(inputCls, "w-40")}>
                            <option value="">All teams</option>
                            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={cn(inputCls, "w-36")}>
                            <option value="">Any status</option>
                            {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                    </div>
                </div>
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 dark:bg-gray-700/40 text-gray-500">
                            <tr>{["Name", "Status", "Receiving leads", "Away from – to", "Daily limit", "Open limit", "Today", "Open leads", "Teams"]
                                .map(h => <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h}</th>)}</tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {shownPeople.map(p => (
                                <PersonRow key={p.user_id} p={p} canEdit={canEdit} onSave={patch => savePerson(p, patch)} />
                            ))}
                            {people && !shownPeople.length && (
                                <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-400">Nobody matches</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {editing && (
                <TeamEditor team={editing} users={users} canEditAll={canEdit}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); loadTeams(); loadPeople(); onChanged?.(); }}
                    create={create} update={update} />
            )}
        </div>
    );
}

function PersonRow({ p, canEdit, onSave }) {
    const [daily, setDaily] = useState(p.daily_cap ?? "");
    const [open, setOpen] = useState(p.open_cap ?? "");
    useEffect(() => { setDaily(p.daily_cap ?? ""); setOpen(p.open_cap ?? ""); }, [p.daily_cap, p.open_cap]);
    const st = STATUS_META[p.status] || STATUS_META.off;
    return (
        <tr>
            <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">{p.name}</td>
            <td className="px-3 py-2"><Pill className={st.cls}>{st.label}</Pill>
                {p.reason && p.status !== "available" && <span className="ml-1 text-[10px] text-gray-400">{p.reason}</span>}</td>
            <td className="px-3 py-2"><Switch checked={p.receiving_leads} disabled={!canEdit}
                onChange={v => onSave({ receiving_leads: v })} label="Receiving leads" /></td>
            <td className="px-3 py-2 whitespace-nowrap">
                <input type="date" value={p.away_from || ""} disabled={!canEdit}
                    onChange={e => onSave({ away_from: e.target.value || null })} className={cn(inputCls, "w-32 inline-block")} />
                <span className="mx-1 text-gray-400">–</span>
                <input type="date" value={p.away_until || ""} disabled={!canEdit}
                    onChange={e => onSave({ away_until: e.target.value || null })} className={cn(inputCls, "w-32 inline-block")} />
            </td>
            <td className="px-3 py-2"><input type="number" min={1} max={500} value={daily} disabled={!canEdit}
                placeholder={p.effective_daily_cap ? String(p.effective_daily_cap) : "—"}
                onChange={e => setDaily(e.target.value)}
                onBlur={() => String(daily) !== String(p.daily_cap ?? "") && onSave({ daily_cap: daily === "" ? null : Number(daily) })}
                className={cn(inputCls, "w-20")} /></td>
            <td className="px-3 py-2"><input type="number" min={1} max={5000} value={open} disabled={!canEdit}
                placeholder={p.effective_open_cap ? String(p.effective_open_cap) : "—"}
                onChange={e => setOpen(e.target.value)}
                onBlur={() => String(open) !== String(p.open_cap ?? "") && onSave({ open_cap: open === "" ? null : Number(open) })}
                className={cn(inputCls, "w-20")} /></td>
            <td className="px-3 py-2 tabular-nums">{p.today}{p.effective_daily_cap ? ` / ${p.effective_daily_cap}` : ""}</td>
            <td className="px-3 py-2 tabular-nums">{p.open}{p.effective_open_cap ? ` / ${p.effective_open_cap}` : ""}</td>
            <td className="px-3 py-2 text-gray-500">{p.teams.map(t => t.name).join(", ") || "—"}</td>
        </tr>
    );
}

function TeamEditor({ team, users, canEditAll, onClose, onSaved, create, update }) {
    const [draft, setDraft] = useState({
        ...team,
        manager_id: team.manager_id ?? team.manager?.id ?? null,
        members: (team.members || []).map(m => ({ user_id: m.user_id, name: m.name, weight: m.weight || 1, in_rotation: m.in_rotation !== false })),
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const memberIds = draft.members.map(m => m.user_id);
    const nameOf = id => users.find(u => u.id === id)?.name || `User ${id}`;

    const save = async () => {
        setSaving(true); setErrors({});
        try {
            const body = {
                ...(canEditAll ? { name: draft.name, description: draft.description, manager_id: draft.manager_id } : {}),
                members: draft.members.map(m => ({ user_id: m.user_id, weight: Number(m.weight) || 1, in_rotation: m.in_rotation })),
            };
            const res = draft.id ? await update(`${ASSIGNMENT_TEAMS_URL}${draft.id}/`, body)
                                 : await create(ASSIGNMENT_TEAMS_URL, body);
            if (res?.status === "success") { ToastMsg("success", res.message); onSaved(); }
            else { setErrors(res?.errors || {}); ToastMsg("error", res?.message || "Could not save the team"); }
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" role="dialog" aria-modal="true">
            <div className="bg-white dark:bg-gray-800 w-full max-w-lg h-full flex flex-col shadow-2xl">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{draft.id ? `Edit ${team.name}` : "New team"}</h2>
                    <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"><X className="h-4 w-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    <Field label="Team name" error={errors.name}>
                        <input value={draft.name} disabled={!canEditAll} maxLength={80}
                            onChange={e => setDraft({ ...draft, name: e.target.value })} className={inputCls} placeholder="e.g. South India" />
                    </Field>
                    <Field label="Manager" help="Gets leads when nobody in the team is free (if that fallback is chosen), and can edit this team." error={errors.manager_id}>
                        <select value={draft.manager_id || ""} disabled={!canEditAll}
                            onChange={e => setDraft({ ...draft, manager_id: e.target.value ? Number(e.target.value) : null })} className={inputCls}>
                            <option value="">No manager</option>
                            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                    </Field>
                    <Field label="Members" help="Weight 2 gets twice the leads of weight 1 (Weighted strategy only). Out of rotation = in the team, but never dealt leads automatically.">
                        <Select mode="multiple" className="w-full" value={memberIds} placeholder="Add reps"
                            options={users.map(u => ({ value: u.id, label: u.name }))}
                            filterOption={(input, o) => (o?.label || "").toLowerCase().includes(input.toLowerCase())}
                            onChange={ids => setDraft({ ...draft, members: ids.map(id =>
                                draft.members.find(m => m.user_id === id) || { user_id: id, weight: 1, in_rotation: true }) })} />
                    </Field>
                    <div className="space-y-1">
                        {draft.members.map((m, i) => (
                            <div key={m.user_id} className="flex items-center gap-2 text-xs">
                                <span className="flex-1 text-gray-800 dark:text-gray-100">{m.name || nameOf(m.user_id)}</span>
                                <label className="flex items-center gap-1 text-gray-500">Weight
                                    <input type="number" min={1} max={10} value={m.weight}
                                        onChange={e => setDraft({ ...draft, members: draft.members.map((x, j) => j === i ? { ...x, weight: e.target.value } : x) })}
                                        className={cn(inputCls, "w-14")} />
                                </label>
                                <label className="flex items-center gap-1 text-gray-500">
                                    <input type="checkbox" checked={m.in_rotation}
                                        onChange={e => setDraft({ ...draft, members: draft.members.map((x, j) => j === i ? { ...x, in_rotation: e.target.checked } : x) })} />
                                    In rotation
                                </label>
                                {errors[`members[${i}].weight`] && <span className="text-red-600">{errors[`members[${i}].weight`][0]}</span>}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                    <button type="button" className={btnOutline} onClick={onClose}>Cancel</button>
                    <button type="button" className={btnPrimary} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save team"}</button>
                </div>
            </div>
        </div>
    );
}
